import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Link as LinkIcon, Sparkles } from 'lucide-react';

import { useIntegrationsList } from '@/lib/api/integrations';

import { WizardOptionCard } from './WizardOptionCard';
import type { QuickstartAnswers, TrafficSource } from './types';

interface StepTrafficSourceProps {
    answers: QuickstartAnswers;
    onChange: (next: Partial<QuickstartAnswers>) => void;
    storeId: string;
    onConnectIntegration: (integrationId: string) => void;
}

interface IntegrationStatus {
    shopify: boolean;
    woocommerce: boolean;
}

function useIntegrationStatus(storeId: string): IntegrationStatus {
    const { data: integrations } = useIntegrationsList(storeId);
    return React.useMemo(() => {
        const list = integrations ?? [];
        return {
            shopify: list.some((i) => i.provider === 'shopify' && i.enabled),
            woocommerce: list.some((i) => i.provider === 'woocommerce' && i.enabled),
        };
    }, [integrations]);
}

export function StepTrafficSource({
    answers,
    onChange,
    storeId,
    onConnectIntegration,
}: StepTrafficSourceProps) {
    const { t } = useTranslation('common');
    const status = useIntegrationStatus(storeId);

    const select = (value: TrafficSource) => onChange({ trafficSource: value });

    // Preselect the first option ("No landing page" — never gated) so users
    // can advance the wizard without an explicit click.
    React.useEffect(() => {
        if (answers.trafficSource === null) {
            onChange({ trafficSource: 'direct' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderDisabledReason = (integrationId: 'shopify' | 'woocommerce') => (
        <div className="flex items-center gap-2">
            <span>{t('funnelQuickstart.trafficSource.notConnected')}</span>
            <span
                role="link"
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    onConnectIntegration(integrationId);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        onConnectIntegration(integrationId);
                    }
                }}
                className="text-primary hover:text-primary/80 inline-flex cursor-pointer items-center gap-1 font-medium underline-offset-2 hover:underline"
            >
                {t('funnelQuickstart.trafficSource.connectNow')}
                <ExternalLink className="h-3 w-3" />
            </span>
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-lg font-semibold">
                    {t('funnelQuickstart.trafficSource.title')}
                </h3>
                <p className="text-muted-foreground text-sm">
                    {t('funnelQuickstart.trafficSource.subtitle')}
                </p>
            </div>

            <div className="flex flex-col gap-3">
                <WizardOptionCard
                    title={t('funnelQuickstart.trafficSource.direct.title')}
                    description={t('funnelQuickstart.trafficSource.direct.description')}
                    icon={<LinkIcon className="h-5 w-5" />}
                    selected={answers.trafficSource === 'direct'}
                    onSelect={() => select('direct')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.trafficSource.shopify.title')}
                    description={t('funnelQuickstart.trafficSource.shopify.description')}
                    logoUrl="/integrations/shopify-icon.svg"
                    selected={answers.trafficSource === 'shopify'}
                    disabled={!status.shopify}
                    disabledReason={!status.shopify ? renderDisabledReason('shopify') : undefined}
                    onSelect={() => status.shopify && select('shopify')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.trafficSource.woocommerce.title')}
                    description={t('funnelQuickstart.trafficSource.woocommerce.description')}
                    logoUrl="/integrations/woo-icon.svg"
                    selected={answers.trafficSource === 'woocommerce'}
                    disabled={!status.woocommerce}
                    disabledReason={!status.woocommerce ? renderDisabledReason('woocommerce') : undefined}
                    onSelect={() => status.woocommerce && select('woocommerce')}
                />
                <WizardOptionCard
                    title={t('funnelQuickstart.trafficSource.landing.title')}
                    description={t('funnelQuickstart.trafficSource.landing.description')}
                    icon={<Sparkles className="h-5 w-5" />}
                    selected={answers.trafficSource === 'landing'}
                    onSelect={() => select('landing')}
                />
            </div>
        </div>
    );
}
